import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { languageCodeValidator } from '../../../shared/validators/custom-validators';
import { PageTitleService } from '../../../core/services/page-title.service';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { LANGUAGE_TABLE_BASE } from '../table-configs';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
  selector: 'app-language-master',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, VioTablesV2Module],
  templateUrl: './language-master.component.html',
  styleUrl: './language-master.component.scss',
})
export class LanguageMasterComponent implements OnInit {
  view: 'list' | 'form' = 'list';

  // List state
  records: any[] = [];
  isListLoading = false;

  // Form state
  form!: FormGroup;
  isEditMode = false;
  editId: string | null = null;
  isSaving = false;
  successMessage = '';
  errorMessage = '';

  // Delete state
  showDeleteConfirm: string | null = null;
  isDeleting = false;
  deleteErrorMessage = '';

  tableConfig: VT2_TABLE_CONFIG = {
    ...LANGUAGE_TABLE_BASE,
    actionButtons: [
      { icon: '✏', title: 'Edit', cssClass: 'vt2-icon-btn--edit', onClick: (row) => this.editRecord(row) },
    ],
  };

  constructor(private fb: FormBuilder, private api: ApiService, private pageTitle: PageTitleService, private dateFmt: DateFormatService) {}

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.pageTitle.set('Language Master', 'Manage languages in the system');
    this.buildForm();
    this.loadList();
  }

  buildForm(): void {
    this.form = this.fb.group({
      languageName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(80)]],
      code:         ['', [Validators.required, languageCodeValidator()]],
    });
  }

  loadList(): void {
    this.isListLoading = true;
    this.api.getLanguages().subscribe({
      next: (data) => {
        this.records = data || [];
        this.isListLoading = false;
      },
      error: () => {
        this.records = [];
        this.isListLoading = false;
      },
    });
  }

  addNew(): void {
    this.isEditMode = false;
    this.editId = null;
    this.form.reset();
    this.successMessage = '';
    this.errorMessage = '';
    this.view = 'form';
    this.pageTitle.set('Language Master', 'Add a new language');
  }

  editRecord(record: any): void {
    this.isEditMode = true;
    this.editId = record._id || record.id;
    this.successMessage = '';
    this.errorMessage = '';
    this.form.patchValue({
      languageName: record.languageName,
      code:         record.code,
    });
    this.view = 'form';
    this.pageTitle.set('Language Master', 'Update language details');
  }

  backToList(): void {
    this.view = 'list';
    this.pageTitle.set('Language Master', 'Manage languages in the system');
    this.loadList();
  }

  get f() { return this.form.controls; }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  confirmDelete(id: string): void {
    this.showDeleteConfirm = id;
    this.deleteErrorMessage = '';
  }

  cancelDelete(): void {
    this.showDeleteConfirm = null;
    this.deleteErrorMessage = '';
  }

  executeDelete(id: string): void {
    this.isDeleting = true;
    this.deleteErrorMessage = '';
    this.api.deleteLanguage(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteConfirm = null;
        this.loadList();
      },
      error: (err: any) => {
        this.isDeleting = false;
        this.deleteErrorMessage = err?.message || 'Failed to delete language.';
      },
    });
  }

  onCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const upper = input.value.toUpperCase();
    this.form.get('code')?.setValue(upper, { emitEvent: false });
    input.value = upper;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.successMessage = '';
    this.errorMessage = '';

    const request$ = this.isEditMode
      ? this.api.updateLanguage({ ...this.form.value, id: this.editId })
      : this.api.saveLanguage(this.form.value);

    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.successMessage = this.isEditMode ? 'Language updated successfully!' : 'Language saved successfully!';
        setTimeout(() => this.backToList(), 1000);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.errorMessage = err?.error?.message || 'Failed to save language.';
      },
    });
  }
}
